import React, { useRef, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import SectionHeading from "../../components/SectionHeading";
import { useSelector } from "react-redux";
import { SIC_Code } from "../../constants/SIC_Code";

const ChartCardPopup = ({ selectedHyperLink, handleCloseModal }) => {
    const { portalUserData } = useSelector((state) => state.reportOrganization);
    const [popupHeight, setPopupHeight] = useState("auto");
    const contentRef = useRef(null);

    // Memoize the computed data to prevent unnecessary recalculations
    const { policyDivisions, sicCodesWithDescriptions } =
        useMemo(() => {
            const allPolicies =
                portalUserData.assignedOrgs?.flatMap(
                    (org) => org.assignedPolicies || []
                ) || [];

            const divisions = [
                ...new Set(
                    allPolicies
                        .filter((p) => p.reportingGroupInd === "N")
                        .map((p) => `${p.policyId}`)
                ),
            ];

            const uniqueSicCodes = [
                ...new Set(
                    allPolicies
                        .map((p) => p?.sicCode)
                        .filter((s) => s != null && s !== "")
                ),
            ];

            // Get SIC code descriptions for display in UI
            const sicCodesWithDesc = uniqueSicCodes?.map((code) => {
                const sicEntry = SIC_Code?.find((sic) => sic?.code === code);
                return sicEntry
                    ? `${code} - ${sicEntry.desc}`
                    : `${code} - undefined`;
            });

            return {
                policyDivisions: divisions,
                sicCodesWithDescriptions: sicCodesWithDesc,
            };
        }, [portalUserData]);

    // Memoize the content objects to prevent recreation on every render
    const content = useMemo(() => {
        const policiesList = {
            title: "Policies",
            bodyListTitle: "Below are the policies that are included:",
            list: policyDivisions,
        };

        const industryPeersList = {
            title: "Industry peers",
            bodyListTitle:
                "Below are the industry peers that your organization is compared with:",
            list: sicCodesWithDescriptions,
        };

        const CONTENT_MAP = {
            policies: policiesList,
            peers: industryPeersList,
        };

        return CONTENT_MAP[selectedHyperLink];
    }, [selectedHyperLink, policyDivisions, sicCodesWithDescriptions]);

    useEffect(() => {
        if (contentRef.current && content) {
            const timeoutId = setTimeout(() => {
                const contentHeight = contentRef.current.scrollHeight;
                const headerHeight = 60;
                const padding = 32;
                const maxHeight = 500;
                const minHeight = 200;

                const calculatedHeight = Math.min(
                    Math.max(contentHeight + headerHeight + padding, minHeight),
                    maxHeight
                );

                setPopupHeight(calculatedHeight);
            }, 0);

            return () => clearTimeout(timeoutId);
        }
    }, [content?.list?.length, selectedHyperLink]);

    // Early return after all hooks
    if (!content) return null; // Optional: handle invalid input

    const { title, bodyListTitle, list } = content;

    return (
        <div
            className="section-card"
            style={{ height: popupHeight, width: 480 }}
        >
            <div className="d-flex justify-content-between pb-16p">
                <SectionHeading heading={title} mb="mb-0p" />
                <i
                    className="fa-solid fa-circle-xmark cursor-pointer"
                    onClick={handleCloseModal}
                    style={{ fontSize: "1.5rem", color: "#015294" }}
                    aria-label="Close"
                    tabIndex={0}
                    role="button"
                />
            </div>
            <div className="chart-popup-header" ref={contentRef}>
                <p>{bodyListTitle}</p>
                <ul
                    className="list-disc list-inside lh-lg custom-scrollbar"
                    style={{
                        overflowY:
                            list && list.length > 10 ? "auto" : "visible",
                        maxHeight: popupHeight === 500 ? 350 : "none",
                    }}
                >
                    {list?.map((item, index) => (
                        <li key={index} style={{ listStyleType: "disc" }}>
                            {item}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};
ChartCardPopup.propTypes = {
    selectedHyperLink: PropTypes.string.isRequired,
    handleCloseModal: PropTypes.func.isRequired,
};

export default ChartCardPopup;
